/**
 * RAČUN KALENDARA — čist, bez DOM-a i bez `Intl` osim za IMENA (D15-A §4).
 *
 * **Nalaz.** Nativni `date`/`time`/`datetime-local` ne slušaju regionalne postavke
 * SUSTAVA nego jezik PREGLEDNIKA: Chrome s engleskim sučeljem nudi "08/14/2026" i
 * "6:30 PM" i onda kad je Windows postavljen na hrvatski, a korisnik ne može
 * ništa. Pult je zato u navigatoru očekivanih dolazaka čitao "Dan dolaska
 * 08/14/2026" (mjesec pa dan!), a dijalog "Pretvori u nalog" nudio je AM/PM
 * brojčanik. `lang="hr"` (dotjerivanje-4 §3) je bio najbolji mogući popravak, ali
 * ne i potpun — Firefox i iOS ga ne slušaju.
 *
 * Zato osoblje od ovog kruga bira kroz VLASTITI picker, koji o obliku ne pregovara:
 *
 * · **datum je `d.M.yyyy`**, uvijek i svugdje — jedan oblik na koji se pult
 *   navikne, a ne oblik po pregledniku;
 * · **sat je 24-satni**, jer je sat mjera, a ne jezik (`lib/format.ts`, `SAT_24`);
 * · **tjedan počinje PONEDJELJKOM**, kao i u kući;
 * · **imena mjeseci i dana idu kroz `Intl`**, na jeziku sučelja — i to je jedina
 *   stvar koju jezik smije mijenjati. Riječi su i18n, brojke su mjera.
 *
 * Vrijednost koja putuje ostaje ISTA kao kod nativnog polja (`yyyy-MM-dd`,
 * `HH:mm`, `yyyy-MM-ddTHH:mm`), pa pozivatelji ne moraju znati da se ispod
 * promijenilo išta.
 */

/** Dan u ISO obliku, onakav kakav je nosilo i nativno polje. */
export type IsoDatum = string

export interface Dijelovi {
  godina: number
  mjesec: number
  dan: number
}

/** `yyyy-MM-dd` → dijelovi; `null` za prazno i za sve što nije taj oblik. */
export function razloziDatum(iso: string | null | undefined): Dijelovi | null {
  if (!iso) return null

  const poklapanje = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.slice(0, 10))

  if (!poklapanje) return null

  const [, g, m, d] = poklapanje
  const godina = Number(g)
  const mjesec = Number(m)
  const dan = Number(d)

  // Podatak koji je prošao oblik, ali ne i kalendar (31.02.), nije datum.
  if (mjesec < 1 || mjesec > 12 || dan < 1 || dan > uMjesecu(godina, mjesec)) return null

  return { godina, mjesec, dan }
}

/** Koliko dana ima mjesec — prijestupnu godinu računa `Date`, ne mi. */
export function uMjesecu(godina: number, mjesec: number): number {
  return new Date(Date.UTC(godina, mjesec, 0)).getUTCDate()
}

/** Dijelovi → `yyyy-MM-dd`. */
export function sloziDatum({ godina, mjesec, dan }: Dijelovi): IsoDatum {
  return `${String(godina).padStart(4, '0')}-${String(mjesec).padStart(2, '0')}-${String(dan).padStart(2, '0')}`
}

/**
 * `yyyy-MM-dd` → `d.M.yyyy`, i to je CIJELI dogovor o obliku. Prazno ostaje
 * prazno; ono što nije datum vraća se doslovno, jer je prikaz koji laže gori od
 * prikaza koji priznaje da ne razumije.
 */
export function uPrikaz(iso: string | null | undefined): string {
  const dijelovi = razloziDatum(iso)

  if (!dijelovi) return iso ?? ''

  return `${dijelovi.dan}.${dijelovi.mjesec}.${dijelovi.godina}`
}

/** Današnji dan po ZIDNOM satu uređaja (kalendar se gleda lokalno, ne u UTC-u). */
export function danas(sada = new Date()): IsoDatum {
  return sloziDatum({
    godina: sada.getFullYear(),
    mjesec: sada.getMonth() + 1,
    dan: sada.getDate(),
  })
}

/** Sat po zidnom satu uređaja, u obliku `HH:mm`. */
export function sada(trenutak = new Date()): string {
  return `${String(trenutak.getHours()).padStart(2, '0')}:${String(trenutak.getMinutes()).padStart(2, '0')}`
}

/**
 * Dan u tjednu, PONEDJELJAK = 0. `Date.getUTCDay()` broji od nedjelje, a resort
 * ne — raspored, smjena i kalendar počinju ponedjeljkom.
 */
export function danTjedna({ godina, mjesec, dan }: Dijelovi): number {
  return (new Date(Date.UTC(godina, mjesec - 1, dan)).getUTCDay() + 6) % 7
}

/**
 * MREŽA MJESECA: tjedni po šest ćelija u retku... zapravo sedam, ponedjeljak
 * prvi. Ćelija je ISO datum, ili `null` za prazninu prije prvog i nakon zadnjeg
 * dana — praznina se crta kao prazna ćelija, a ne kao dan susjednog mjeseca:
 * pult koji tapne "31." misleći da je ovaj mjesec ne smije dobiti prošli.
 */
export function mreza(godina: number, mjesec: number): (IsoDatum | null)[][] {
  const prvi = danTjedna({ godina, mjesec, dan: 1 })
  const dana = uMjesecu(godina, mjesec)

  const celije: (IsoDatum | null)[] = [
    ...Array.from({ length: prvi }, () => null),
    ...Array.from({ length: dana }, (_, redni) => sloziDatum({ godina, mjesec, dan: redni + 1 })),
  ]

  while (celije.length % 7 !== 0) celije.push(null)

  return Array.from({ length: celije.length / 7 }, (_, tjedan) =>
    celije.slice(tjedan * 7, tjedan * 7 + 7),
  )
}

/** Mjesec pomaknut za `korak` (prelazak godine uključen). */
export function pomakMjeseca(
  godina: number,
  mjesec: number,
  korak: number,
): { godina: number; mjesec: number } {
  const ukupno = godina * 12 + (mjesec - 1) + korak

  return { godina: Math.floor(ukupno / 12), mjesec: (ukupno % 12) + 1 }
}

/**
 * Smije li se taj dan izabrati. `min`/`max` su ISO datumi i uspoređuju se kao
 * NIZOVI — `yyyy-MM-dd` je oblik u kojem abecedni poredak jest kronološki, pa
 * pretvorba u `Date` ovdje ne kupuje ništa osim zone koja može pomaknuti dan.
 */
export function uRasponu(iso: IsoDatum, min?: string, max?: string): boolean {
  if (min && iso < min) return false
  if (max && iso > max) return false

  return true
}

/**
 * IMENA IDU KROZ `Intl`, na jeziku sučelja — jedina stvar u ovom modulu koja
 * ovisi o jeziku. Nedjelja je zadnja, jer tjedan počinje ponedjeljkom.
 */
export function imenaDana(jezik: string, oblik: 'short' | 'long' = 'short'): string[] {
  const format = new Intl.DateTimeFormat(jezik, { weekday: oblik, timeZone: 'UTC' })

  // 2024-01-01 je ponedjeljak — sidro koje ne ovisi o današnjem danu.
  return Array.from({ length: 7 }, (_, pomak) =>
    format.format(new Date(Date.UTC(2024, 0, 1 + pomak))),
  )
}

/** Ime mjeseca s godinom, za zaglavlje kalendara ("kolovoz 2026."). */
export function imeMjeseca(godina: number, mjesec: number, jezik: string): string {
  return new Intl.DateTimeFormat(jezik, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(godina, mjesec - 1, 1)),
  )
}

/**
 * `yyyy-MM-ddTHH:mm` razložen na dva polja; nedostajuće je prazan niz. Sekunde se
 * odbacuju — nijedan naš ekran ih ne pita, a poslužitelj ih ne traži.
 */
export function razdvojiTrenutak(vrijednost: string | null | undefined): {
  datum: string
  vrijeme: string
} {
  if (!vrijednost) return { datum: '', vrijeme: '' }

  const [datum = '', ostatak = ''] = vrijednost.split('T')

  return { datum, vrijeme: ostatak.slice(0, 5) }
}

/**
 * Dva polja natrag u `yyyy-MM-ddTHH:mm`.
 *
 * **Bez datuma nema trenutka** — prazno je prazno. Bez SATA ima: dan izabran, a
 * sat još ne, znači ponoć, jer bi inače izbor dana izgledao kao da se ništa nije
 * dogodilo.
 */
export function sloziTrenutak(datum: string, vrijeme: string): string {
  if (datum === '') return ''

  return `${datum}T${vrijeme === '' ? '00:00' : vrijeme}`
}
